import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Flag, X, Check, Loader2 } from 'lucide-react';
import * as api from '../lib/api';

const REASONS = [
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'spam', label: 'Spam' },
  { value: 'hate_speech', label: 'Hate speech' },
  { value: 'other', label: 'Other' },
];

const ReportModal = ({ open, postId, onClose }) => {
  const [reason, setReason] = useState('inappropriate');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const modalRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const el = modalRef.current;
    if (!el) return;
    const focusable = Array.from(el.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
    focusable[0]?.focus();
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') { onClose?.(); return; }
      if (e.key !== 'Tab' || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  const handleSubmit = async () => {
    setSubmitting(true); setError('');
    try {
      await api.reportPost(postId, { reason, details: details.trim() || null });
      setDone(true);
      setTimeout(() => { onClose?.(); reset(); }, 1500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to submit report');
    }
    setSubmitting(false);
  };

  const reset = () => { setReason('inappropriate'); setDetails(''); setDone(false); setError(''); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="report-modal-title"
            onClick={e => e.stopPropagation()}
            className="bg-card rounded-arch-xl shadow-2xl max-w-md w-full"
            data-testid="report-modal">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Flag className="w-5 h-5 text-primary" aria-hidden="true" />
                <h3 id="report-modal-title" className="font-heading text-lg text-primary">Report this post</h3>
              </div>
              <button onClick={onClose} aria-label="Close report dialog" className="min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-primary/10 rounded-full">
                <X className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>

            {done ? (
              <div className="p-6 text-center" data-testid="report-success">
                <div className="w-12 h-12 rounded-full bg-green-100 mx-auto flex items-center justify-center mb-3">
                  <Check className="w-6 h-6 text-green-700" />
                </div>
                <p className="text-sm text-text-main font-medium">Thank you, this post has been reported.</p>
                <p className="text-xs text-text-muted mt-1">Our moderators will review it shortly.</p>
              </div>
            ) : (
              <>
                <div className="p-5 space-y-3">
                  <p className="text-xs text-text-muted">Why are you reporting this post?</p>
                  {REASONS.map(r => (
                    <label key={r.value} className="flex items-center gap-3 cursor-pointer p-2 rounded-arch hover:bg-primary/5">
                      <input type="radio" name="report-reason" value={r.value}
                        checked={reason === r.value} onChange={() => setReason(r.value)}
                        data-testid={`report-reason-${r.value}`} />
                      <span className="text-sm text-text-main">{r.label}</span>
                    </label>
                  ))}
                  <textarea value={details} onChange={e => setDetails(e.target.value)}
                    rows={2} maxLength={500} placeholder="Optional: add a short detail (max 500 chars)"
                    className="w-full mt-2 px-3 py-2 rounded-arch border border-primary/20 bg-white dark:text-gray-800 text-sm"
                    data-testid="report-details-input" />
                  {error && <p className="text-xs text-red-600">{error}</p>}
                </div>
                <div className="p-4 border-t border-border flex justify-end gap-2">
                  <button onClick={onClose} className="px-4 py-2 rounded-arch text-sm hover:bg-primary/5">Cancel</button>
                  <button onClick={handleSubmit} disabled={submitting}
                    className="px-4 py-2 rounded-arch text-sm font-medium bg-primary text-white hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50"
                    data-testid="report-submit-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Flag className="w-4 h-4" aria-hidden="true" />}
                    Submit Report
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReportModal;
